import { useState, useEffect } from "react";
import { createUseStyles } from "react-jss";
import ReactDropzone from "react-dropzone";
import { v4 as uuid } from "uuid";

import { deckService, ttsService } from "services";
import { DeckCommon, DeckDto, DeckRequestDto } from "dtos/shard/deck.dto";
import { UserDto } from "dtos/user.dto";

import variables from "styles/variables";
import widgets from "styles/widgets";
import { useResources } from "hooks/resources.hook";
import { useEditorActions } from "hooks/editor-actions.hook";
import { useEditorPreview } from "hooks/editor-preview.hook";
import EditorControls from "../editor-controls";
import { UploadState } from "types/upload-state";
import { StatusDto } from "dtos/status.dto";
import { ErrorDto } from "dtos/error.dto";

const styles = createUseStyles({
    container: {
        width: "100%",
    },
    dropzone: {
        width: "calc(100% - 2em)",
        height: "auto",
        margin: "1em 1em",
        textAlign: "center",

        backgroundColor: variables.colors.neutral + "AA",
        boxShadow: `0 0 10px 10px ${variables.colors.neutral + "AA"}`,
    },
});

export default function DeckEditor(props: {
    user?: UserDto, // Optional dto of the user.
    deck?: Partial<DeckDto>,
    preview?: Partial<DeckDto>,
    onPreviewUpdate?: (previewDto: Partial<DeckDto>) => void,
    onClose?: () => void,
    onSave?:(deck: DeckDto) => void,
}): JSX.Element
{
    // Dtos:
    const freshCommon: DeckCommon = {
        name: props.deck?.name ?? "",
        description: props.deck?.description ?? "",
        userId: props.deck?.userId ?? props.user?.id ?? "",
        private: props.deck?.private ?? true,
        coverCardEntryId: props.deck?.coverCardEntryId ?? "",
        deckEntries: props.deck?.deckEntries ?? [],
    };
    const freshRequestDto: DeckRequestDto = {
        id: props.deck?.id,
        ...freshCommon,
    };
    const freshPreviewDto: Partial<DeckDto> = props.preview ? structuredClone(props.preview) : props.deck ? structuredClone(props.deck) : {
        id: undefined,
        ...freshCommon,
    };

    // States:
    const [deckUploadState, setDeckUploadState] = useState(undefined as UploadState | undefined);

    // Hooks:
    const resources = useResources(props.user);
    const [previewDto, requestDto, previewUpdate, setRequestDto] = useEditorPreview(freshPreviewDto, freshRequestDto, props.onPreviewUpdate);
    const [onTextChange, onNumberChange, onRteChange, onCheckboxChange, onPopup] = useEditorActions(props.user, "deck", resources, requestDto, previewDto, setRequestDto, props.onPreviewUpdate);

    // Effects:
    useEffect(() => {
        setRequestDto(freshRequestDto);
        setDeckUploadState(undefined);
    }, [props.deck]);

    // Upload:
    useEffect(() => {
        if (deckUploadState && !deckUploadState.error) {
            ttsService.importDeck(deckUploadState.file, deckUploadState.requestId);
        }
    }, [deckUploadState]);

    // Upload Success:
    useEffect(() => ttsService.addListener((event: string, dto: StatusDto | ErrorDto | undefined) => {
        console.log(event, dto);
        if (!dto) {
            return;
        }
        switch (event) {
            case "tts.import":
                if (deckUploadState && dto.request_id === deckUploadState.requestId) {
                    setDeckUploadState(undefined);
                    // eslint-disable-next-line no-restricted-globals
                    location.reload();
                }
                break;
            case "tts.import.error":
                if (deckUploadState && dto.request_id === deckUploadState.requestId) {
                    setDeckUploadState({
                        ...deckUploadState,
                        error: true,
                    });
                }
                break;
        }
    }), [deckUploadState]);

    // Styles:
    const widgetClasses = widgets();
    const classes = styles();

    // Decks:
    const reset = () => {
        setRequestDto(freshRequestDto);
        if (props.onPreviewUpdate) {
            props.onPreviewUpdate(props.deck ?? previewDto);
        }
    };

    const deckUpload = <T extends File>(files: T[]) => {
        if (!files.length) {
            return;
        }
        setDeckUploadState({
            file: files[0],
            requestId: uuid(),
        });
    };

    // Return Component:
    return (
        <div className={`${widgetClasses.form} ${classes.container}`}>
            <div className={`${widgetClasses.row}`}>
                <label>Name</label>
                <input id="name" type="text" value={requestDto.name ?? ""} onChange={onTextChange} />
            </div>

            <div className={`${widgetClasses.row}`}>
                <label>Private</label>
                <input id="private" type="checkbox" checked={requestDto.private ?? ""} onChange={onCheckboxChange} />
            </div>

            <h4>Description</h4>
            <textarea id="description" value={requestDto.description ?? ""} onChange={onTextChange} />

            <hr/>
            <EditorControls
                editorName="deck"
                service={deckService}
                subject={props.deck}
                subjectName={props.deck?.name ?? ""}
                requestDto={requestDto}
                onSave={props.onSave}
                onReset={reset}
                onClose={props.onClose}
            />
            <hr/>

            {requestDto.id ? "" : <ReactDropzone onDrop={(accepted, rejections, event) => deckUpload(accepted)}>
                {({ getRootProps }) => {
                    return (
                        <div className={`${classes.dropzone}`} {...getRootProps()}>
                            <p>{deckUploadState ? deckUploadState.error ? "Invalid Deck Requires: TTS JSON, 50 Cards Max" : "Uploading TTS Deck..." : "Or Drop a TTS Deck Here to Import (50 Cards Max)"}</p>
                        </div>
                    )
                }}
            </ReactDropzone>}
        </div>
    );
}
